<template>
  <div>
    <div class="d-flex mb-3 align-items-end">
      <h1 class="h4 primary--text">
        {{ $t('views.orders.order_groups') }}
      </h1>
      <div class="ml-auto">
        <v-btn
          outlined
          large
          @click="toggleFilters"
        >
          <v-icon left>
            mdi-filter-variant
          </v-icon>
          {{ $t('common.filter') }}
        </v-btn>
      </div>
    </div>
    <table-filters
      v-if="filtersShown"
      :filters="filters"
      :no-items-found="noItemsFound"
      @search="getItems(true)"
    />
    <v-data-table
      fixed-header
      :headers="headers"
      :items="items"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 50, 100],
      }"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :loading-text="$t('common.loading_data')"
    >
      <template v-slot:item.day="{ item }">
        {{ item.day | moment("DD.MM.YYYY") }}
      </template>
      <template v-slot:item.status="{ item }">
        {{ $t(`enums.order_statuses.${item.status}`) }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-hover v-slot:default="{hover}">
          <router-link
            class="icon-link"
            :to="`/orders/order-groups/${item.id}`"
          >
            <v-icon
              class="mr-4"
              :class="hover ? '' : 'text--disabled'"
              color="primary"
              size="20"
            >
              mdi-open-in-new
            </v-icon>
          </router-link>
        </v-hover>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import TableFilters from '@/components/common/TableFilters.vue';
import { mongoRegexI } from '@/utils/mongo-regex';

export default {
  name: 'OrderGroupList',
  components: {
    TableFilters,
  },
  data() {
    return {
      total: 0,
      items: [],
      loading: false,
      options: {
        sortBy: ['day'],
        sortDesc: [true],
        page: 1,
      },
      headers: [
        { text: this.$t('common.date'), value: 'day' },
        { text: this.$t('common.supplier'), value: 'supplier' },
        { text: this.$t('common.plant'), value: 'plant' },
        { text: this.$t('common.status'), value: 'status' },
        {
          text: this.$t('common.actions'), value: 'actions', sortable: false, width: 150, align: 'center',
        },
      ],
      filters: [{
        label: this.$t('common.supplier'),
        key: 'supplier',
        value: '',
        requiredForSupplier: true,
      }, {
        label: this.$t('common.plant'),
        key: 'plant',
        value: '',
        requiredForSupplier: true,
      }, {
        label: this.$t('common.status'),
        key: 'status',
        value: '',
        type: 'select',
        cosls: 2,
        options: [{
          value: 'viewed',
          text: this.$t('enums.order_statuses.viewed'),
        }, {
          value: 'posted',
          text: this.$t('enums.order_statuses.posted'),
        }],
      }, {
        label: this.$t('common.dateFrom'),
        key: 'dateFrom',
        value: '',
        type: 'date',
      }, {
        label: this.$t('common.dateTo'),
        key: 'dateTo',
        value: '',
        type: 'date',
      }],
      filtersShown: true,
      noItemsFound: false,
    };
  },
  computed: {
    ...mapGetters(['business']),
    params: {
      get() {
        const {
          sortBy, sortDesc, page, itemsPerPage,
        } = this.options;

        const params = {};

        params.query = this.filters.reduce((acc, it) => {
          if (it.type === 'date' && it.value) {
            if (!acc.day) {
              acc.day = {};
            }

            if (it.key === 'dateFrom') {
              acc.day.$gte = it.value;
            }

            if (it.key === 'dateTo') {
              acc.day.$lte = it.value;
            }
            return acc;
          }

          if (it.type === 'select' && it.value) {
            acc[it.key] = it.value;
            return acc;
          }

          if (it.value) {
            if (!this.business && ['supplier', 'plant'].includes(it.key)) {
              acc[it.key] = it.value;
            } else {
              acc[it.key] = mongoRegexI(it.value);
            }
          }

          return acc;
        }, {});

        if (itemsPerPage !== -1) {
          params.pageSize = itemsPerPage;
          params.page = page;
        }

        if (sortBy.length) {
          params.sort = `${sortDesc[0] ? '-' : ''}${sortBy[0]}`;
        }

        params.fields = 'id day supplier plant status';

        return params;
      },
    },
  },
  watch: {
    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },
  },
  activated() {
    this.getItems();
  },
  methods: {
    async getItems(filtered) {
      this.loading = true;

      if (filtered) {
        this.options.page = 1;
      }

      try {
        const { data = {} } = await this.$http.get('/orders', { params: this.params });

        this.items = data.rows;
        this.total = data.total;
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loading = false;
      }
    },
    toggleFilters() {
      if (this.items.length) {
        this.filtersShown = !this.filtersShown;
        return;
      }
      this.filtersShown = true;
    },
  },
};
</script>

<style lang="scss">
.fixed-column-table {
  display: flex;
  white-space: nowrap;
}
</style>
